// https://ria.pacificlife.com/enroll-ui/
import React, { useState, useEffect } from "react";
import Select from "react-select";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import { QuestionCircle } from "react-bootstrap-icons";
import ReactTooltip from "react-tooltip";
import Toast from "react-bootstrap/Toast";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import axios from "axios";
import moment from "moment";
import formatSSN from "./utils/formatSSN";
import formatPhone10 from "./utils/formatPhone10";
import {
    IAR,
    IAR_PROSPECT,
    IAR_ASSISTANT,
    CUSTODIAN,
    ROLE_OPTIONS,
    REQUIRED_FIELDS,
    RIA_LOGIN_PAGE_URL_PATH,
    REGISTRATION_PENDING_URL_PATH,
    RIA_STRATEGIC_ALLIANCES_URL,
    REGISTRATION_SUCCESSFUL_URL_PATH
} from "./constants";
import {
    emailValidation,
    userNameValidation,
    emailVerifyMatch,
    ssnValidation
} from "./validation";
import { analytics } from "./analytics";
import { Header, Footer, Disclaimer } from "./components";

const defaultNonPersonal = {
    firmId: "",
    crd: "",
    ssn4: "",
    contractNumber: "",
    tin: "",
    dob: "",
    id: "" // username
};

const ProspectReg = () => {
    const [user, setUser] = useState({
        role: "",
        firstName: "",
        lastName: "",
        email: "",
        verifyEmail: "",
        phone: "",
        firmId: "",
        crd: "",
        ssn4: "",
        contractNumber: "",
        tin: "",
        dob: "",
        id: "",
        activeContract: null,
        plRelation: null
    });
    const [errors, setErrors] = useState({
        firmId: "",
        crd: "",
        email: "",
        verifyEmail: "",
        ssn4: "",
        id: "",
        tin: "",
        phone: ""
    });
    const [toastMessage, setToastMessage] = useState("");
    const [submitDisabled, setSubmitDisabled] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        analytics();
    }, []);

    useEffect(() => {
        if (user.role === "") {
            setSubmitDisabled(true);
            return;
        }
        // check if all fields are filled out
        let role = user.role;
        if (role === IAR && user.activeContract === false) {
            role = IAR_PROSPECT;
        }
        const requiredFields = REQUIRED_FIELDS[role];
        for (const element of requiredFields) {
            const fieldName = element;
            if (!user[fieldName]) {
                setSubmitDisabled(true);
                return;
            }
            if (
                role === "iar_prospect" &&
                user.activeContract === false &&
                user.plRelation === false
            ) {
                if (user.phone.length < 14) {
                    setSubmitDisabled(true);
                    return;
                }
            }
        }
        // check if there is any error
        const errorFields = Object.keys(errors);
        for (const element of errorFields) {
            const fieldName = element;
            if (errors[fieldName] !== "") {
                setSubmitDisabled(true);
                return;
            }
        }

        setSubmitDisabled(false);
    }, [user, errors]);

    const handleChange = (e) => {
        const { name } = e.target;
        let { value } = e.target;

        const radioFields = new Set(["activeContract", "plRelation"]);
        if (radioFields.has(name)) {
            value = value === "true";
        }

        let payload = { [name]: value };

        // reset some user state and errors state if certain fields value changes;
        if (
            (name === "role" && value !== user.role) ||
            name === "activeContract" ||
            name === "plRelation"
        ) {
            payload = { ...payload, ...defaultNonPersonal };
            if (name === "role") {
                payload.activeContract = null;
                payload.plRelation = null;
            }
            if (name === "activeContract") {
                payload.plRelation = null;
            }

            setErrors((prev) => ({
                ...prev,
                firmId: "",
                crd: "",
                ssn4: "",
                id: "",
                tin: ""
            }));
        }

        if (name === "phone") {
            value = formatPhone10(value);
            if (value.length > 14) {
                return;
            } else {
                payload = { [name]: value };
            }
        }
        setUser((prev) => ({ ...prev, ...payload }));
    };

    const handleBlur = async (e) => {
        const { name, value } = e.target;
        let errorMessage = "";
        if (name === "firmId") {
            errorMessage =
                value.length >= 2 ? "" : "Firm ID must be at least 2 digits";
        }
        if (name === "crd") {
            errorMessage =
                value.length >= 4
                    ? ""
                    : "Advisor CRD must be at least 4 digits";
        }

        if (name === "email") {
            const emailValid = emailValidation(value);
            errorMessage = emailValid ? "" : "Enter valid email";
        }

        if (name === "verifyEmail") {
            errorMessage = emailVerifyMatch(user.email, value)
                ? ""
                : "Email addresses do not match";
        }

        if (name === "ssn4") {
            const valid = /\d{4}/g.test(value);
            errorMessage = valid ? "" : "Enter valid last 4 digit of SSN";
        }

        if (name === "id") {
            const valid = userNameValidation(value);
            const tooShortOrLong = value.length < 6 || value.length > 8;
            const hasSpecialCharOrWhiteSpace = /\W|\D/g.test(value);
            if (valid) {
                errorMessage = "";
            } else if (tooShortOrLong) {
                errorMessage =
                    "Username must be between 6 and 8 characters long";
            } else if (hasSpecialCharOrWhiteSpace) {
                errorMessage =
                    "Username must not contain spaces or special characters";
            }
        }

        if (name === "tin") {
            const valid = ssnValidation(value);
            errorMessage = valid ? "" : "Enter valid SSN";
        }

        if (name === "phone") {
            errorMessage =
                value.length < 14
                    ? "Please enter a valid 10 digit phone number"
                    : "";
        }

        setErrors((prev) => ({ ...prev, [name]: errorMessage }));
    };

    const handleCancel = () => {
        document.location.href = RIA_LOGIN_PAGE_URL_PATH;
    };

    const formatPayload = () => {
        const payload = {};
        let role = user.role;
        if (role === IAR && user.activeContract === false) {
            role = IAR_PROSPECT;
        }
        const requiredFields = REQUIRED_FIELDS[role];
        payload.userRole = role;
        for (const element of requiredFields) {
            const fieldName = element;
            if (fieldName === "firmId") {
                payload.firmId = [user.firmId];
            } else if (fieldName === "crd") {
                payload.crdList = [user.crd];
            } else if (fieldName === "tin") {
                payload.tin = user.tin.replace(/-/g, "");
            } else if (fieldName === "dob") {
                payload.dob = moment(user.dob).format("YYYY-MM-DD");
            } else if (fieldName === "phone") {
                payload[fieldName] = user[fieldName];
            } else {
                payload[fieldName] = user[fieldName];
            }
        }
        return payload;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        let role = user.role;
        if (role === IAR && user.activeContract === false) {
            role = IAR_PROSPECT;
        }
        const payload = formatPayload();
        setSubmitting(true);
        try {
            await axios({
                method: "POST",
                url: "/enroll/ria/user/register",
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json"
                },
                data: JSON.stringify(payload)
            });
            if (role === IAR_ASSISTANT || role === CUSTODIAN) {
                document.location.href = REGISTRATION_PENDING_URL_PATH;
            } else {
                document.location.href = REGISTRATION_SUCCESSFUL_URL_PATH;
            }
        } catch (error) {
            setToastMessage(
                error.response?.data?.errorMessage ||
                    "Something went wrong. Please try again later."
            );
        }
        setSubmitting(false);
    };

    const closeToast = () => {
        setToastMessage("");
    };

    return (
        <>
            {toastMessage !== "" && (
                <Toast onClose={closeToast} className="toast-cont">
                    <Toast.Header>
                        <strong className="me-auto">{toastMessage}</strong>
                    </Toast.Header>
                </Toast>
            )}
            <Header />
            <div className="mainMyAccount">
                <h1 className="heading" style={{ marginBottom: "2%" }}>
                    Create an Account
                </h1>
                <div className="subHeading" style={{ marginBottom: "3%" }}>
                    Register to view supporting product information, manage
                    individual client contracts, and get access to support.
                </div>
                <Row style={{ marginBottom: "2%" }}>
                    <Form.Label className="sub-title">
                        Please select your role
                    </Form.Label>
                    <Select
                        className="select"
                        placeholder="Select..."
                        onChange={(e) =>
                            handleChange({
                                target: {
                                    name: "role",
                                    value: e.value
                                }
                            })
                        }
                        options={ROLE_OPTIONS}
                        data-track="analytics"
                    />
                </Row>
                <Row>
                    <Col sm={6}>
                        <label className="sub-title">
                            Personal Information
                        </label>
                    </Col>
                </Row>
                <Form>
                    <Row>
                        <Col md={6}>
                            <Form.Label>First Name</Form.Label>
                            <Form.Control
                                name="firstName"
                                value={user.firstName}
                                maxLength={25}
                                onChange={handleChange}
                            />
                        </Col>
                        <Col md={6}>
                            <Form.Label>Last Name</Form.Label>
                            <Form.Control
                                name="lastName"
                                value={user.lastName}
                                maxLength={35}
                                onChange={handleChange}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col md={6}>
                            <ReactTooltip multiline />
                            <Form.Label className="emailClass">
                                Email Address
                                <QuestionCircle
                                    className="iconClass"
                                    data-tip="Your password link will be sent to the email address you supply here"
                                    tabIndex="-1"
                                />
                            </Form.Label>
                            <Form.Control
                                name="email"
                                value={user.email}
                                maxLength={60}
                                onChange={handleChange}
                                onBlur={handleBlur}
                            />
                            <span className="validation-error-message">
                                {errors.email}
                            </span>
                        </Col>
                        <Col md={6}>
                            <Form.Label>Verify Email Address</Form.Label>
                            <Form.Control
                                name="verifyEmail"
                                value={user.verifyEmail}
                                maxLength={60}
                                onChange={handleChange}
                                onPaste={(e) => e.preventDefault()}
                                onBlur={handleBlur}
                            />
                            <span className="validation-error-message">
                                {errors.verifyEmail}
                            </span>
                        </Col>
                    </Row>
                    {user.role === IAR && (
                        <div
                            className="bus-info-row"
                            style={{ marginBottom: "3%" }}
                        >
                            <Row>
                                <Col sm={6}>
                                    <label className="sub-title">
                                        Business Information
                                    </label>
                                </Col>
                            </Row>
                            <Form.Check
                                type="radio"
                                label="I have an active contract with Pacific Life"
                                name="activeContract"
                                id="haveActiveContract"
                                value={true}
                                defaultChecked={user.activeContract === true}
                                onClick={handleChange}
                                style={{ marginBottom: "1%" }}
                            />
                            <Form.Check
                                type="radio"
                                label="I do not have an active or pending contract with Pacific Life"
                                name="activeContract"
                                id="dontHaveActiveContract"
                                value={false}
                                defaultChecked={user.activeContract === true}
                                onClick={handleChange}
                            />
                            {user.activeContract === false && (
                                <div className="relationRadioGroup">
                                    <Form.Check
                                        type="radio"
                                        className="radioLabel"
                                        label="I do have an existing relationship with a Pacific Life preferred distribution partner"
                                        name="plRelation"
                                        value={true}
                                        defaultChecked={
                                            user.plRelation === true
                                        }
                                        onClick={handleChange}
                                        style={{ marginTop: "2%" }}
                                    />
                                    <div className="radioSublabel">
                                        View a complete list{" "}
                                        <a
                                            href={RIA_STRATEGIC_ALLIANCES_URL}
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            here
                                        </a>
                                    </div>
                                    {user.plRelation && (
                                        <div className="pl-relation-message">
                                            Please contact your Pacific Life
                                            advisory consultant at (866)
                                            441-2354
                                        </div>
                                    )}
                                    <Form.Check
                                        type="radio"
                                        className="radioLabel noRelation"
                                        label="I do not have an existing relationship with a Pacific Life preferred distribution partner and wish to work directly with Pacific Life"
                                        name="plRelation"
                                        value={false}
                                        defaultChecked={
                                            user.plRelation === false
                                        }
                                        onClick={handleChange}
                                        style={{ marginBottom: "3%" }}
                                    />
                                </div>
                            )}
                            {(user.activeContract ||
                                user.plRelation === false) && (
                                <div>
                                    <Row>
                                        <Col sm={6}>
                                            <label className="sub-title">
                                                Identity Verification
                                            </label>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col md={6}>
                                            <ReactTooltip multiline />
                                            <Form.Label className="firmIdStyle">
                                                Firm ID/CRD
                                                <QuestionCircle
                                                    className="iconClass"
                                                    data-tip="Enter the firm identification number or FINRA-issued CRD (Central Registration Depository) number used with Pacific Life"
                                                    tabIndex="-1"
                                                />
                                            </Form.Label>
                                            <Form.Control
                                                placeholder="12345"
                                                maxLength="8"
                                                name="firmId"
                                                value={user.firmId}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                            />
                                            <span className="validation-error-message">
                                                {errors.firmId}
                                            </span>
                                        </Col>
                                        <Col md={6}>
                                            <ReactTooltip multiline />
                                            <Form.Label className="advisorCRD">
                                                Advisor CRD
                                                <QuestionCircle
                                                    className="iconClass"
                                                    data-tip="Enter your FINRA-issued CRD (Central Registration Depository) number"
                                                    tabIndex="-1"
                                                />
                                            </Form.Label>
                                            <Form.Control
                                                placeholder="1234567"
                                                maxLength="7"
                                                name="crd"
                                                value={user.crd}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                            />
                                            <span className="validation-error-message">
                                                {errors.crd}
                                            </span>
                                        </Col>
                                    </Row>
                                    {!user.activeContract && (
                                        <Row>
                                            <Col md={6}>
                                                <Form.Label className="phone">
                                                    Phone Number
                                                </Form.Label>
                                                <Form.Control
                                                    placeholder="(123) 456-7890"
                                                    name="phone"
                                                    value={formatPhone10(
                                                        user.phone
                                                    )}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                />
                                                <span className="validation-error-message">
                                                    {errors.phone}
                                                </span>
                                            </Col>
                                        </Row>
                                    )}
                                </div>
                            )}
                            {user.activeContract && (
                                <Row>
                                    <Col md={6}>
                                        <ReactTooltip multiline />
                                        <Form.Label className="contractNumber">
                                            Contract Number
                                            <QuestionCircle
                                                className="iconClass"
                                                data-tip="Enter one of your client's active Pacific Life annuity contract number"
                                                tabIndex="-1"
                                            />
                                        </Form.Label>
                                        <Form.Control
                                            placeholder="AB12345678"
                                            maxLength="10"
                                            name="contractNumber"
                                            value={user.contractNumber}
                                            onChange={handleChange}
                                        />
                                    </Col>
                                    <Col md={6}>
                                        <ReactTooltip multiline />
                                        <Form.Label className="socialSecurityNumber">
                                            Social Security Number
                                            <QuestionCircle
                                                className="iconClass"
                                                data-tip="Enter your social security number for security purposes and to verify your identity"
                                                tabIndex="-1"
                                            />
                                        </Form.Label>
                                        <Form.Control
                                            name="tin"
                                            value={formatSSN(user.tin)}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            placeholder="555-55-5555"
                                            maxLength="11"
                                        />
                                        <span className="validation-error-message">
                                            {errors.tin}
                                        </span>
                                    </Col>
                                </Row>
                            )}
                        </div>
                    )}
                    {(user.role === IAR_ASSISTANT ||
                        user.role === CUSTODIAN) && (
                        <div>
                            <Row>
                                <Col sm={6}>
                                    <label className="sub-title">
                                        Identity Verification
                                    </label>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={6}>
                                    <ReactTooltip multiline />
                                    <Form.Label className="firmIdStyle">
                                        Firm ID/CRD
                                        <QuestionCircle
                                            className="iconClass"
                                            data-tip="Enter the firm identification number or FINRA-issued CRD (Central Registration Depository) number used with Pacific Life"
                                            tabIndex="-1"
                                        />
                                    </Form.Label>
                                    <Form.Control
                                        name="firmId"
                                        value={user.firmId}
                                        onChange={handleChange}
                                        placeholder="12345"
                                        maxLength="8"
                                        onBlur={handleBlur}
                                    />
                                    <span className="validation-error-message">
                                        {errors.firmId}
                                    </span>
                                </Col>
                                <Col md={6}>
                                    <ReactTooltip multiline />
                                    <Form.Label className="advisorCRD">
                                        Last 4 of SSN
                                        <QuestionCircle
                                            className="iconClass"
                                            data-tip="Enter the last four digits of your social security number for security purposes and to verify your identity"
                                            tabIndex="-1"
                                        />
                                    </Form.Label>
                                    <Form.Control
                                        placeholder="4444"
                                        name="ssn4"
                                        value={user.ssn4}
                                        maxLength="4"
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                    />
                                    <span className="validation-error-message">
                                        {errors.ssn4}
                                    </span>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={6}>
                                    <ReactTooltip multiline />
                                    <Form.Label className="contractNumber">
                                        Date of Birth
                                    </Form.Label>
                                    <DatePicker
                                        selected={user.dob}
                                        className="react-datepicker-ignore-onclickoutside"
                                        onChange={(dob) =>
                                            handleChange({
                                                target: {
                                                    name: "dob",
                                                    value: dob
                                                }
                                            })
                                        }
                                        showYearDropdown={true}
                                        openToDate={new Date("1985/07/01")}
                                    />
                                </Col>
                            </Row>
                        </div>
                    )}
                    {((user.role && user.role !== IAR) ||
                        user.activeContract ||
                        user.plRelation === false) && (
                        <>
                            <Row>
                                <Col md={6}>
                                    <label className="sub-title">
                                        Login Information
                                    </label>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={6}>
                                    <ReactTooltip multiline />
                                    <Form.Label className="createUsername">
                                        User Name
                                        <QuestionCircle
                                            tabIndex="-1"
                                            className="iconClass"
                                            data-tip="Remember this! You will need it along with your password to access your account information"
                                        />
                                    </Form.Label>
                                    <Form.Control
                                        name="id"
                                        autoComplete="off"
                                        maxLength={8}
                                        value={user.id}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                    />
                                    <span className="validation-error-message">
                                        {errors.id}
                                    </span>
                                </Col>
                                <Col md={6}>
                                    <div className="sublabel">
                                        Must be between 6 and 8 characters
                                        <br />
                                        May contain letters and numbers
                                        <br />
                                        No spaces or special characters
                                    </div>
                                </Col>
                            </Row>
                        </>
                    )}
                    {user.role === "iar" && (
                        <div id="regAccessFormMsg">
                            Please note that the information submitted must
                            match the{" "}
                            <a
                                href="/content/dam/paclife/rsd/annuities/public/ria/32069.pdf"
                                target="_blank"
                                rel="noreferrer"
                            >
                                Advisory Onboarding Agreement
                            </a>
                        </div>
                    )}
                    <div className="buttons-myaccount">
                        <Button
                            variant="secondary"
                            onClick={handleCancel}
                            onKeyPress={(e) => {
                                if (e.key === "Enter") {
                                    e.preventDefault();
                                }
                            }}
                            data-track="analytics"
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            type="submit"
                            onClick={handleSubmit}
                            data-track="analytics"
                            disabled={
                                user.plRelation === true ||
                                submitDisabled ||
                                submitting
                            }
                        >
                            {submitting ? "Registering" : "Register"}
                        </Button>
                    </div>
                </Form>
                <div id="login" className="link-underline">
                    <a href={RIA_LOGIN_PAGE_URL_PATH}>
                        Already have an account?
                    </a>
                </div>
                <Disclaimer />
            </div>
            <Footer />
        </>
    );
};

export default ProspectReg;
